// Ultimate Card Game (UCG) - Bot AI battle estimator
//
// "Who wins the fight at the front if it happens now?" - the question the
// bot's plasma-sum heuristics (computeUnitTacticalValue(), the wave planner's
// fixed 30-plasma threshold) cannot answer, because they ignore what a unit
// can actually hit and survive: air versus ground, deflection versus Energy,
// splash against a clump, range, a Shade nobody can see
// (docs/AI_MASTERPLAN_2026-10.md, B4/B5/B19, section 6.3).
//
// Lanchester's square law, kept coarse on purpose:
//
//   strength(A against B) = F(A against B) x EHP(A against B)
//   ratio                 = strength(A) / strength(B)      > 1: A wins
//
//   F    summed damage per second, each unit's rate scaled by how much of the
//        OPPONENT it can engage (targetsAir / targetsGround, undetected
//        cloaked units cannot be hit), by their deflection (Energy ignores
//        it), by splash against their ground mass, and by card bonuses
//        (Devil Patrol against Marines, Tesla's deflection scaling).
//   EHP  summed hit points, each divided by what its own deflection shaves
//        off the opponent's non-Energy damage.
//
// Only units that can be in the fight count: a unit's weight falls from 1 to
// 0 with the time it needs to reach the contact point (see `horizon`), and the
// defender's base turret joins a fight at its own base.
//
// Pure functions over plain records, so the same code scores live units
// (describeUnit), cards the bot could still play (describeCard) and the
// stored snapshots tools/ai-eval/estimator.mjs calibrates it on. It reads
// nothing from the bot and changes nothing: wiring it into the wave planner
// and the nuke controller is Phase 1.2 onwards.

import { computeUnitTacticalValue } from './perception.js';

// Calibrated against simulated fights (npm run ai:eval -- estimator --tune):
// only `horizon` and `rangeK` moved from their first physical guesses; a full
// 11-parameter fit gained just 0.5 points more on held-out fights and drifted
// to implausible edge values, so the rest stay as reasoned. Numbers:
// docs/AI_MASTERPLAN_2026-10.md, "Stand nach Phase 1.1".
export const ESTIMATOR_PARAMS = Object.freeze({
  // Seconds a unit may need to reach the front and still count in full; the
  // weight falls linearly to 0 at twice that. Calibrated for "who wins the
  // fight that is on right now": 4 s beat 12 s by 3.5 points of accuracy.
  // Reinforcements still on their way need a much larger horizon (a unit
  // played at the base takes about 20 s to arrive) - callers ask for that
  // per call through `opts.params`.
  horizon: 4,
  // Extra damage per additional enemy ground body a splash unit reaches
  // (scaled by its radius against a 60 px reference), and the cap on it.
  splashK: 0.12,
  splashCap: 1.0,
  // A kamikaze's burst is spread over this many seconds of the fight.
  kamikazeSpread: 3,
  // Base turret (js/data/balance.js baseDamage / 1.4 s) and how close to its
  // base a fight must be for it to join.
  turretDps: 12.9,
  turretReach: 360,
  // An undetected cloaked unit absorbs this many times its hit points.
  cloakEhpMul: 2,
  // Share of the damage a reflecting unit hands back, counted as hit points.
  reflectK: 0.5,
  // Bonus for out-ranging: 1 + rangeK x min(1, (range - 120) / 360). Lanchester
  // ignores that a ranged unit fires before a melee one arrives; this was the
  // second-biggest calibration gain (+2.4 points together with `horizon`).
  rangeK: 1.5,
  // Lanchester's square law is F x EHP; the exponents let calibration soften
  // or sharpen either factor (1 and 1 = the classic law).
  fExp: 1,
  ehpExp: 1,
  // A side with nothing that can fight counts as this strong (avoids 0/0).
  floor: 1
});

const AIR_REACH_DEFAULT = true;

// --- records ---------------------------------------------------------------

// One fighter as the estimator sees it. `s` is a plain stat bag in pixels and
// seconds (what a live Unit exposes, and what describeCard() derives from a
// card); card-only quirks that change the damage rate are folded in here.
function makeRecord(s) {
  const card = s.cardData || {};
  const damage = s.damage || 0;
  const attackSpeed = s.attackSpeed || 1;
  const accuracy = s.accuracy ?? 1;
  const isKamikaze = !!card.isKamikaze;
  const canAttack = card.canAttack !== false;

  let dps = 0;
  let burst = 0;
  if (canAttack && damage > 0) {
    if (isKamikaze) {
      burst = damage * accuracy;
    } else {
      dps = (damage / attackSpeed) * accuracy;
      if (s.pods > 0) dps = (s.pods * damage / attackSpeed) * accuracy; // Enforcer Vulture: 8 independent pods
      if (card.dualWeapon) dps *= 2;
      if (card.burstDuration > 0 && card.reloadDuration > 0) {
        dps *= card.burstDuration / (card.burstDuration + card.reloadDuration);
      } else if (card.reloadDuration > 0) {
        dps *= attackSpeed / (attackSpeed + card.reloadDuration); // Orchestra: one barrage, then a long reload
      }
    }
  }
  // Tethers hold on and burn; they are not subject to a miss roll.
  if (card.hasTether && card.tetherDps) dps = Math.max(dps, card.tetherDps);
  if (card.hasDualTether && card.dualTetherDps) dps = Math.max(dps, card.dualTetherDps);

  return {
    id: s.id ?? null,
    team: s.team,
    x: s.x,
    y: s.y ?? 0,
    hp: Math.max(0, s.hp || 0),
    dps,
    burst,
    energy: s.projectileType === 'Energy',
    scaling: !!card.deflectionScaling,
    antiMarine: !!card.antiMarineBonus,
    canGround: s.targetsGround !== false,
    canAir: s.targetsAir !== false && AIR_REACH_DEFAULT,
    isAir: !!s.isAir,
    isStructure: !!s.isStructure,
    isMarine: s.unitType === 'Marine' || s.unitType === 'HyperMarine',
    range: s.range || 0,
    splash: s.splash || 0,
    speed: s.speed || 0,
    defl: Math.min(0.85, s.deflectionRating || 0),
    reflect: card.reflectDamageRatio || 0,
    hidden: !!s.hidden,
    value: s.value || 0
  };
}

// A live unit (js/engine/unit.js). `battlefield` decides whether a cloaked
// unit is currently invisible to the other side.
export function describeUnit(u, battlefield) {
  const opponent = u.team === 'western' ? 'eastern' : 'western';
  const hidden = !!(u.isCloaked && !battlefield?.isPositionDetected?.(opponent, u.x, u.y));
  return makeRecord({
    id: u.id,
    team: u.team,
    x: u.x,
    y: u.y,
    hp: u.hp,
    damage: u.damage,
    attackSpeed: u.attackSpeed,
    accuracy: u.accuracy,
    projectileType: u.projectileType,
    targetsAir: u.targetsAir,
    targetsGround: u.targetsGround,
    isAir: u.isAir,
    isStructure: u.isStructure,
    unitType: u.cardData?.unitType || u.unitType,
    range: u.range,
    splash: u.splashRadius,
    speed: u.speed,
    deflectionRating: u.deflectionRating,
    cardData: u.cardData,
    pods: u.vulturePods ? u.vulturePods.length : 0,
    hidden,
    value: computeUnitTacticalValue(u)
  });
}

// A card the bot could still play: spawned at `x`, at full health, with the
// same px conversions the engine applies (js/engine/unit.js: range and splash
// x24, speed x26).
export function describeCard(card, team, x, y = 0) {
  const isStickman = card.unitType === 'Stickman';
  return makeRecord({
    id: null,
    team,
    x,
    y,
    hp: card.hp,
    damage: card.damage,
    attackSpeed: card.attackSpeed,
    accuracy: card.accuracy,
    projectileType: card.projectileType,
    targetsAir: card.targetsAir,
    targetsGround: card.targetsGround,
    isAir: !!(card.isAir || card.elementType === 'Air'),
    isStructure: !!(card.isStructure || card.type === 'Structure'),
    unitType: card.unitType,
    range: (card.range || 1) * 24,
    splash: isStickman ? 24 : (card.splashRadius || 0) * 24,
    speed: (card.speed || 0) * 26,
    deflectionRating: card.deflectionRating,
    cardData: card,
    pods: card.unitType === 'EnforcerVulture' ? 8 : 0,
    hidden: !!card.isCloaked,
    value: card.cost || 6
  });
}

// --- estimation --------------------------------------------------------------

// Where the two armies meet: halfway between the two most advanced mobile
// units (a side marches east/west: western units advance to larger x).
export function contactPoint(own, enemy, ownTeam) {
  const advancing = ownTeam === 'western';
  const front = (list, forward) => {
    let best = null;
    for (const r of list) {
      if (r.isStructure || r.speed <= 0) continue;
      if (best === null || (forward ? r.x > best : r.x < best)) best = r.x;
    }
    return best;
  };
  const ownFront = front(own, advancing);
  const enemyFront = front(enemy, !advancing);
  if (ownFront === null || enemyFront === null) return null;
  return (ownFront + enemyFront) / 2;
}

function arrivalWeight(r, contactX, horizon) {
  const d = Math.abs(r.x - contactX);
  let t;
  if (r.speed > 0) t = Math.max(0, d - r.range) / r.speed;
  else t = d <= r.range ? 0 : Infinity;
  if (t <= horizon) return 1;
  if (t >= 2 * horizon) return 0;
  return 1 - (t - horizon) / horizon;
}

// Mass-weighted composition of one side as its opponent has to face it.
function composition(list, weights) {
  let mass = 0, air = 0, ground = 0, marine = 0, deflMass = 0, dps = 0, energyDps = 0, bodies = 0;
  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    const w = weights[i];
    if (w <= 0) continue;
    const dpsW = r.dps * w;
    dps += dpsW;
    if (r.energy) energyDps += dpsW;
    if (r.hidden) continue; // cannot be targeted, so it shapes nothing the opponent aims at
    const m = r.hp * w;
    mass += m;
    if (r.isAir) air += m; else { ground += m; bodies += w; }
    if (r.isMarine) marine += m;
    deflMass += r.defl * m;
  }
  return {
    mass,
    airShare: mass > 0 ? air / mass : 0,
    groundShare: mass > 0 ? ground / mass : 0,
    marineShare: mass > 0 ? marine / mass : 0,
    meanDefl: mass > 0 ? deflMass / mass : 0,
    energyShare: dps > 0 ? energyDps / dps : 0,
    groundBodies: bodies
  };
}

// Damage per second `list` brings to bear on a side shaped like `against`,
// and the hit points `list` can absorb from damage shaped like `against`'s.
function strengthAgainst(list, weights, against, p) {
  let f = 0;
  let burst = 0;
  let ehp = 0;
  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    const w = weights[i];
    if (w <= 0) continue;

    // What of the opponent this unit can engage at all.
    const reach = against.groundShare * (r.canGround ? 1 : 0) + against.airShare * (r.canAir ? 1 : 0);
    if (r.dps > 0 || r.burst > 0) {
      let mul = reach;
      mul *= r.energy ? 1 : (1 - against.meanDefl);
      if (r.scaling) mul *= 1 + against.meanDefl;
      if (r.antiMarine) mul *= 1 + against.marineShare;
      if (r.splash > 0 && r.canGround) {
        mul *= 1 + Math.min(p.splashCap, p.splashK * Math.max(0, against.groundBodies - 1) * (r.splash / 60));
      }
      if (p.rangeK) mul *= 1 + p.rangeK * Math.min(1, Math.max(0, (r.range - 120) / 360));
      f += r.dps * mul * w;
      burst += r.burst * mul * w;
    }

    // What it can take: deflection only helps against non-Energy damage.
    let hp = r.hp / (1 - r.defl * (1 - against.energyShare));
    if (r.reflect) hp *= 1 + p.reflectK * r.reflect;
    if (r.hidden) hp *= p.cloakEhpMul;
    ehp += hp * w;
  }
  return { f: f + burst / p.kamikazeSpread, ehp };
}

// `own` and `enemy` are records (describeUnit / describeCard); `opts`:
//   ownTeam  'western' | 'eastern' - which way `own` advances
//   bases    { western: { x }, eastern: { x } } - lets a defending base turret join
//   contactX force the contact point (the live bot knows its own front)
//   params   overrides of ESTIMATOR_PARAMS
// Returns ratio > 1 when `own` wins; `contact` false when one side has
// nobody to fight (then the ratio says only who is there at all).
export function estimateBattle(own, enemy, opts = {}) {
  const p = opts.params ? { ...ESTIMATOR_PARAMS, ...opts.params } : ESTIMATOR_PARAMS;
  const ownTeam = opts.ownTeam || 'western';
  const enemyTeam = ownTeam === 'western' ? 'eastern' : 'western';

  const contactX = opts.contactX ?? contactPoint(own, enemy, ownTeam);
  if (contactX === null || contactX === undefined) {
    const anyOwn = own.some(r => r.hp > 0);
    const anyEnemy = enemy.some(r => r.hp > 0);
    return { ratio: anyOwn === anyEnemy ? 1 : (anyOwn ? Infinity : 0), contact: false, contactX: null, own: null, enemy: null };
  }

  const ownW = own.map(r => arrivalWeight(r, contactX, p.horizon));
  const enemyW = enemy.map(r => arrivalWeight(r, contactX, p.horizon));
  const ownComp = composition(own, ownW);
  const enemyComp = composition(enemy, enemyW);

  const a = strengthAgainst(own, ownW, enemyComp, p);
  const b = strengthAgainst(enemy, enemyW, ownComp, p);

  // A defending base turret fires at whoever fights near it. It adds damage,
  // not hit points: the base is what the fight is about.
  const turretFor = (team) => {
    const baseX = opts.bases?.[team]?.x;
    return baseX !== undefined && Math.abs(contactX - baseX) <= p.turretReach ? p.turretDps : 0;
  };
  const ownTurret = turretFor(ownTeam);
  const enemyTurret = turretFor(enemyTeam);
  if (ownTurret) a.f += ownTurret * (1 - enemyComp.meanDefl);
  if (enemyTurret) b.f += enemyTurret * (1 - ownComp.meanDefl);

  const ownStrength = Math.pow(a.f, p.fExp) * Math.pow(a.ehp, p.ehpExp);
  const enemyStrength = Math.pow(b.f, p.fExp) * Math.pow(b.ehp, p.ehpExp);
  const ratio = (ownStrength + p.floor) / (enemyStrength + p.floor);
  return {
    ratio,
    contact: true,
    contactX,
    own: { strength: ownStrength, dps: a.f, ehp: a.ehp, turret: ownTurret > 0 },
    enemy: { strength: enemyStrength, dps: b.f, ehp: b.ehp, turret: enemyTurret > 0 }
  };
}

// The measure the bot used before: plasma value on each side, nothing else.
// Kept as the calibration baseline the estimator has to beat.
export function valueRatio(own, enemy) {
  let a = 0, b = 0;
  for (const r of own) a += r.value;
  for (const r of enemy) b += r.value;
  return (a + 1) / (b + 1);
}

// Splits a live board into the two sides' records.
export function describeBoard(battlefield, ownTeam) {
  const own = [];
  const enemy = [];
  for (const u of battlefield.units) {
    if (u.isDead || u.isGoldenStatue || u.isCinematic || u.indestructible || u.isCocoon) continue;
    (u.team === ownTeam ? own : enemy).push(describeUnit(u, battlefield));
  }
  return { own, enemy };
}
